/*
================================================================================
|                                                                              |
|  FILE: src/sections/ecorevivepage/SDGShowcase.js                             |
|  PURPOSE: Unique SDG showcase section for all EcoRevive project SDGs.       |
|                                                                              |
================================================================================
*/
import React, { useState } from 'react';

const SDG_DATA = [
    {
        number: "SDG 2",
        title: "Zero Hunger",
        description: "End hunger, achieve food security and improved nutrition and promote sustainable agriculture",
        color: "from-green-400 to-green-600",
        icon: "🌾",
        impact: "Enhanced food security through land restoration"
    },
    {
        number: "SDG 6", 
        title: "Clean Water & Sanitation",
        description: "Ensure availability and sustainable management of water and sanitation for all",
        color: "from-blue-400 to-blue-600",
        icon: "💧",
        impact: "Water purification and river restoration"
    },
    {
        number: "SDG 8",
        title: "Decent Work & Economic Growth", 
        description: "Promote sustained, inclusive and sustainable economic growth, full and productive employment and decent work for all",
        color: "from-red-400 to-red-600",
        icon: "💼",
        impact: "Rural employment and community development"
    },
    {
        number: "SDG 13",
        title: "Climate Action",
        description: "Take urgent action to combat climate change and its impacts",
        color: "from-green-500 to-green-700",
        icon: "🌱",
        impact: "Climate-smart community foundation"
    },
    {
        number: "SDG 14",
        title: "Life Below Water",
        description: "Conserve and sustainably use the oceans, seas and marine resources for sustainable development",
        color: "from-blue-500 to-blue-700",
        icon: "🐟",
        impact: "River ecosystem restoration"
    },
    {
        number: "SDG 15",
        title: "Life on Land",
        description: "Protect, restore and promote sustainable use of terrestrial ecosystems, sustainably manage forests, combat desertification, and halt and reverse land degradation and halt biodiversity loss",
        color: "from-green-600 to-green-800",
        icon: "🌳",
        impact: "Forest reserve rehabilitation"
    }
];

export default function SDGShowcase() {
    const [selectedSDG, setSelectedSDG] = useState(0);

    return (
        <section id="sdg-showcase" className="py-20 text-white" style={{ backgroundColor: '#111827' }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold mb-4">
                        Aligning with UN Sustainable Development Goals
                    </h2>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                        Project EcoRevive is not just a project—it's a commitment to future generations, 
                        directly supporting 6 critical UN SDGs
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                    <div className="space-y-6">
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-4">
                                <div className={`w-16 h-16 bg-gradient-to-r ${SDG_DATA[selectedSDG].color} rounded-full flex items-center justify-center text-2xl`}>
                                    {SDG_DATA[selectedSDG].icon}
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold">{SDG_DATA[selectedSDG].number}</h3>
                                    <p className="text-lg text-gray-300">{SDG_DATA[selectedSDG].title}</p>
                                </div>
                            </div>
                            <p className="text-gray-300 leading-relaxed mb-4">
                                {SDG_DATA[selectedSDG].description}
                            </p>
                            <div className="bg-white/5 rounded-lg p-4">
                                <p className="text-sm font-semibold text-green-400 mb-1">EcoRevive Impact:</p>
                                <p className="text-sm text-gray-300">{SDG_DATA[selectedSDG].impact}</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {SDG_DATA.map((sdg, index) => (
                            <button
                                key={sdg.number}
                                onClick={() => setSelectedSDG(index)}
                                className={`p-6 rounded-xl transition-all duration-300 ${
                                    selectedSDG === index 
                                        ? 'bg-white/20 scale-105' 
                                        : 'bg-white/5 hover:bg-white/10'
                                }`}
                            >
                                <div className="text-center">
                                    <div className={`w-12 h-12 bg-gradient-to-r ${sdg.color} rounded-full flex items-center justify-center mx-auto mb-3 text-lg`}>
                                        {sdg.icon}
                                    </div>
                                    <p className="text-sm font-bold">{sdg.number}</p>
                                    <p className="text-xs text-gray-400 mt-1">{sdg.title.split(' ')[0]}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="text-center">
                    <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full">
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                        <span className="text-sm font-semibold">Active Project</span>
                    </div>
                    {/* <p className="text-gray-400 mt-4">
                        Photos from Field Inspection & Assessment Report • July, 2025
                    </p> */}
                </div>
            </div>
        </section>
    );
} 